<template>
  <div class="min-h-screen">
    <AppHeader />
    
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
      <div class="container-custom">
        <div v-if="loading" class="text-center">
          <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
        </div>
        <div v-else class="text-center">
          <h1 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {{ heroSection?.data?.title || 'Our Services' }}
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ heroSection?.data?.subtitle || 'Comprehensive software development services tailored to your business needs.' }}
          </p>
        </div>
      </div>
    </section>
    
    <!-- Services Grid -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <SkeletonLoader v-for="i in 6" :key="i" type="card" height="400px" />
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <router-link 
            v-for="service in services" 
            :key="service.id" 
            :to="{ name: 'ServiceDetail', params: { id: service.uid } }"
            class="group card overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
          >
            <!-- Banner Image -->
            <div class="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
              <img 
                v-if="service.image"
                :src="getBannerUrl(service.image)" 
                :alt="service.name"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div v-else class="w-full h-full flex items-center justify-center">
                <svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              
              <!-- Gradient Overlay -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              


              <!-- View Details Badge -->
              <div class="absolute bottom-4 right-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-primary-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-lg">
                View Details →
              </div>
            </div>

            <!-- Card Content -->
            <div class="p-6">
              <h3 class="font-heading text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">{{ service.name }}</h3>
              <p class="text-gray-600 mb-4 line-clamp-2">{{ service.short_description }}</p>
              
              <!-- Features Preview -->
              <div v-if="service.features && service.features.length > 0" class="space-y-2 mb-4">
                <div v-for="(feature, index) in service.features.slice(0, 3)" :key="index" class="flex items-start text-sm text-gray-600">
                  <svg class="w-4 h-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  {{ feature }}
                </div>
                <p v-if="service.features.length > 3" class="text-xs text-primary-600 font-medium ml-6">
                  +{{ service.features.length - 3 }} more features
                </p>
              </div>
              
              <!-- Pricing & Timeline -->
              <div class="flex items-center justify-between pt-4 border-t border-gray-200">
                <div>
                  <p class="text-xs text-gray-500 mb-1">Starting from</p>
                  <p class="text-lg font-bold text-primary-600">${{ service.starting_price }}</p>
                </div>
                <div class="text-right">
                  <p class="text-xs text-gray-500 mb-1">Timeline</p>
                  <p class="text-sm font-semibold text-gray-700">{{ service.timeline_min_weeks }}-{{ service.timeline_max_weeks }} weeks</p>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </section>
    
    <!-- Process Section - How We Work -->
    <section class="relative section-padding overflow-hidden">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-primary-50/30 to-secondary-50/30"></div>
      
      <!-- Decorative Elements -->
      <div class="absolute top-0 right-0 w-96 h-96 bg-primary-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div class="absolute bottom-0 left-0 w-96 h-96 bg-secondary-200/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      
      <div class="container-custom relative z-10">
        <div v-if="loading" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="600px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <SkeletonLoader v-for="i in 4" :key="i" type="card" height="320px" />
          </div>
        </div>

        <div v-else>
          <!-- Section Header -->
          <div class="text-center mb-16">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
              How We Work
            </div>
            <h2 class="font-heading text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              {{ processSection?.data?.title || 'Our Process' }}
            </h2>
            <p class="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {{ processSection?.data?.description || 'A proven methodology that delivers exceptional results through strategic planning and flawless execution.' }}
            </p>
          </div>
          
          <!-- Process Steps Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            <!-- Connection Lines (Desktop Only) -->
            <div class="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary-200 to-transparent" style="width: calc(100% - 8rem); margin-left: 4rem;"></div>
            
            <div 
              v-for="(step, index) in processSteps" 
              :key="index" 
              class="process-step-card group relative"
            >
              <!-- Card -->
              <div class="relative h-full bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100 transition-all duration-500 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:border-primary-200">
                <!-- Step Number Badge -->
                <div class="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                  <div class="relative">
                    <!-- Outer Glow Ring -->
                    <div class="absolute inset-0 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <!-- Number Circle -->
                    <div class="relative w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-xl transform transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
                      <span class="relative z-10">{{ index + 1 }}</span>
                      
                      <!-- Animated Ring -->
                      <div class="absolute inset-0 rounded-full border-2 border-white/30 animate-ping opacity-0 group-hover:opacity-100"></div>
                    </div>
                  </div>
                </div>

                <!-- Icon Container -->
                <div class="mt-8 mb-6 flex justify-center">
                  <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center transform transition-all duration-500 group-hover:rotate-6 group-hover:scale-110">
                    <svg class="w-10 h-10 text-primary-600 transition-colors duration-500 group-hover:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path 
                        v-if="index === 0"
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-width="2" 
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      ></path>
                      <path 
                        v-else-if="index === 1"
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-width="2" 
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      ></path>
                      <path 
                        v-else-if="index === 2"
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-width="2" 
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      ></path>
                      <path 
                        v-else
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-width="2" 
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      ></path>
                    </svg>
                  </div>
                </div>

                <!-- Content -->
                <div class="text-center">
                  <h3 class="font-heading text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-primary-600">
                    {{ step.title }}
                  </h3>
                  <p class="text-gray-600 text-sm leading-relaxed">
                    {{ step.description }}
                  </p>
                </div>

                <!-- Decorative Corner Accent -->
                <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary-100/50 to-transparent rounded-bl-full rounded-tr-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div class="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-secondary-100/50 to-transparent rounded-tr-full rounded-bl-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              <!-- Arrow Connector (Desktop Only) -->
              <div v-if="index < processSteps.length - 1" class="hidden lg:block absolute top-24 -right-4 z-20">
                <svg class="w-8 h-8 text-primary-400 transform transition-all duration-500 group-hover:text-primary-600 group-hover:scale-125" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Bottom CTA -->
          <div class="mt-16 text-center">
            <div class="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-full border border-primary-100 shadow-lg">
              <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span class="text-gray-700 font-medium">Proven process with 100+ successful projects</span>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- CTA Section -->
    <section class="relative overflow-hidden bg-gray-50 section-padding">
      <!-- Background Elements matching Hero -->
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] rounded-full bg-purple-200/30 blur-3xl"></div>
      </div>

      <div class="container-custom text-center relative z-10">
        <div v-if="loading" class="space-y-6">
          <SkeletonLoader type="title" width="400px" class="mx-auto" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
          <SkeletonLoader type="button" class="mx-auto" />
        </div>

        <div v-else class="max-w-4xl mx-auto">
          <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
            <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
            Let's Work Together
          </div>
          <h2 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Ready to Get Started?
          </h2>
          <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let's discuss how our services can help transform your business.
          </p>
          <div class="flex flex-wrap gap-4 justify-center">
            <button 
              @click="contactWhatsApp"
              class="btn bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-4 shadow-xl shadow-green-500/20 hover:shadow-green-500/40 flex items-center gap-2"
            >
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp Order
            </button>
            <router-link 
              to="/order-service"
              class="btn bg-primary-600 hover:bg-primary-700 text-white text-lg px-8 py-4 shadow-xl shadow-primary-500/20 hover:shadow-primary-500/40 flex items-center gap-2"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
              </svg>
              Order Now
            </router-link>
            <router-link to="/contact" class="btn bg-white border-2 border-gray-200 text-gray-900 hover:border-primary-500 text-lg px-8 py-4 flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Email Us
            </router-link>
          </div>
        </div>
      </div>
    </section>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useContentStore } from '@/stores/content'
import { useServiceStore } from '@/stores/services'
import { useOrderStore } from '@/stores/orders'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const contentStore = useContentStore()
const serviceStore = useServiceStore()
const orderStore = useOrderStore()
const loading = ref(true)

const contactWhatsApp = () => {
  orderStore.openWhatsApp("Hi! I'm interested in your services.")
}

onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('services'),
      serviceStore.fetchServices(),
      serviceStore.fetchCategories()
    ])
  } catch (error) {
    console.error('Error loading services page content:', error)
  } finally {
    loading.value = false
  }
})

const servicesSections = computed(() => contentStore.pageSections.services || [])
const heroSection = computed(() => servicesSections.value.find(s => s.section === 'hero'))
const processSection = computed(() => servicesSections.value.find(s => s.section === 'process'))

const services = computed(() => {
  const serviceStore = useServiceStore()
  return serviceStore.getActiveServices()
})

const getServiceIcon = (serviceName) => {
  const iconMap = {
    'Web Development': 'WebIcon',
    'Mobile Development': 'MobileIcon',
    'UI/UX Design': 'DesignIcon',
    'Cloud Solutions': 'CloudIcon',
    'E-commerce': 'EcommerceIcon',
    'Consulting': 'ConsultingIcon'
  }
  return iconMap[serviceName] || 'WebIcon'
}

const processSteps = computed(() => {
  const steps = processSection.value?.data?.steps
  if (steps && Array.isArray(steps)) return steps
  return [
    { title: 'Discovery', description: 'Understanding your needs and goals' },
    { title: 'Planning', description: 'Creating a detailed project roadmap' },
    { title: 'Development', description: 'Building your solution with best practices' },
    { title: 'Delivery', description: 'Launching and supporting your project' }
  ]
})



const getBannerUrl = (imagePath) => {
  return serviceStore.getBannerUrl(imagePath)
}
</script>

<script>
export default {
  components: {
    WebIcon: {
      template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>`
    },
    MobileIcon: {
      template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>`
    },
    DesignIcon: {
      template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path></svg>`
    },
    CloudIcon: {
      template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"></path></svg>`
    },
    EcommerceIcon: {
      template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>`
    },
    ConsultingIcon: {
      template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>`
    }
  }
}
</script>
